import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useRouter } from "next/router";
import ProgressSection from "@components/progressSection";
import Header from "@widgets/header";
import { executeActions, getWidgetSpecificProps } from "@utils/index";
import { formatToINR } from "@utils/inrFormatter";
import { resolvePath } from "@utils/resolvePath";
import { isWebView } from "@utils/commonUtils";
import {
  BackButton,
  CarImage,
  CarBackground,
  Copy,
  DriveHeading,
  DriveSection,
  DesktopBody,
  DesktopHeader,
  DesktopProgress,
  HeroVisual,
  InsuranceHeading,
  Logo,
  Nav,
  Page,
  PlansAvailable,
  PriceColumn,
  PriceComparison,
  PriceLabel,
  PriceValue,
  PrimaryButton,
  SavingsCard,
  SecondaryButton,
  Shell,
  TopSection,
  Vs
} from "./styles";

const DEFAULT_LOGO = "/images/ACKODrive_logo.webp";
const CAR_BACKGROUND_FACES = "/images/car_background_faces.webp";
const FALLBACK_CAR_IMAGE =
  "https://ackodrive-prod.ackoassets.com/_next_static/images/placeHolder-FallbackImage.png";
const DESKTOP_PROGRESS_STEPS = [
  {
    name: "Car details",
    status: "complete" as const
  },
  {
    name: "Personal details",
    status: "complete" as const
  },
  {
    name: "Plan selection",
    status: "progress" as const
  },
  {
    name: "Verify and review",
    status: "pending" as const
  }
];

interface AckoDriveUpsellPageProps {
  id?: string;
  actions?: {
    see_all_plans_on_click?: ActionConfig[];
    see_how_we_do_it_on_click?: ActionConfig[];
    back_on_click?: ActionConfig[];
  };
  is_visible?: boolean;
  plansCount?: number | string;
  startingPremium?: number | string;
  vehicleName?: string;
  carImageUrl?: string;
  ackodrivePrice?: number | string;
  dealerPrice?: number | string;
  landingPageUrl?: string;
  phone?: string | number;
  cityName?: string;
  pincode?: string | number;
  brandName?: string;
  modelName?: string;
  variantName?: string;
  ackoDriveLogo?: string;
  plansLabel?: string;
  primaryCtaLabel?: string;
  secondaryCtaLabel?: string;
  insuranceHeading?: string;
  ackoDriveHeading?: string;
}

interface ActionConfig {
  type?: string;
  args?: Record<string, unknown>;
  funCall?: (args?: Record<string, unknown>) => void;
}

interface ReduxState {
  configuration?: {
    normalisedConfig?: Record<string, unknown>;
  };
}

const mapStateToProps = (
  state: ReduxState,
  ownProps: AckoDriveUpsellPageProps
) => {
  return getWidgetSpecificProps(
    state.configuration?.normalisedConfig,
    ownProps.id || ""
  ) || {};
};

const toNumber = (value?: number | string | null) => {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number") return value;
  return Number(String(value).replace(/[^\d.-]/g, "")) || 0;
};

const formatAmount = (value?: number | string | null) => {
  const amount = toNumber(value);
  if (!amount) return "";
  return formatToINR(amount, true);
};

const openAckoDriveDeeplink = (url: string) => {
  const deeplinkPayload = {
    action: "open_deeplink",
    action_value: {
      deeplink: `acko.com://web_page_v2?url=${encodeURIComponent(url)}`
    }
  };

  window?.flutter_inappwebview?.callHandler?.(
    "WebViewConnect",
    JSON.stringify(deeplinkPayload)
  );
};

const AckoDriveUpsellPage = (props: AckoDriveUpsellPageProps) => {
  const router = useRouter();
  const [shouldShowBackButton, setShouldShowBackButton] = useState(false);
  const [displayCustomersCount] = useState(
    () => Math.floor(Math.random() * 11) + 5
  );
  const {
    actions,
    is_visible,
    plansCount,
    startingPremium,
    vehicleName,
    carImageUrl,
    ackodrivePrice,
    dealerPrice,
    landingPageUrl,
    phone,
    cityName,
    pincode,
    modelName,
    plansLabel = "plans available",
    primaryCtaLabel = "See all plans",
    secondaryCtaLabel = "See how we do it",
    insuranceHeading,
    ackoDriveHeading
  } = props;

  const numericSavings = Math.max(
    toNumber(dealerPrice) - toNumber(ackodrivePrice),
    0
  );
  const displayVehicleName = vehicleName || modelName || "new car";
  const displayPlansCount = toNumber(plansCount) || plansCount || 3;
  const displayStartingPremium = formatAmount(startingPremium) || "₹28,000";
  const displayAckoDrivePrice = formatAmount(ackodrivePrice);
  const displayDealerPrice = formatAmount(dealerPrice);
  const displaySavings = formatAmount(numericSavings);

  const ackoDriveBaseUrl = process.env.ENVIRONMENT == "development"
    ? "https://ad-customer-website.uat.ackodrive.com"
    : "https://ackodrive.com";

const ackoDriveUpsellUrl = landingPageUrl ? `${ackoDriveBaseUrl}/upsell/${landingPageUrl}/` : "";

  useEffect(() => {
    setShouldShowBackButton(!isWebView());
  }, []);

  const handleSeeAllPlans = () => {
    executeActions(actions?.see_all_plans_on_click);
  };

  const handleBack = () => {
    executeActions(actions?.back_on_click, { router });
  };

  const handleSeeHow = () => {
    executeActions(actions?.see_how_we_do_it_on_click);

    if (!ackoDriveUpsellUrl || typeof window === "undefined") return;

    const targetUrl = new URL(ackoDriveUpsellUrl);
    targetUrl.searchParams.set("current_node", "plan_selection_node");
    const proposalEkey = router.query?.proposal_ekey || router.query?.ekey;
    if (typeof proposalEkey === "string") {
      targetUrl.searchParams.set("proposal_ekey", proposalEkey);
    }
    if (phone) {
      targetUrl.searchParams.set("phone", String(phone));
    }
    if (cityName) {
      targetUrl.searchParams.set("city", cityName);
    } else if (pincode) {
      targetUrl.searchParams.set("pincode", String(pincode));
    }

    if (isWebView() && window?.flutter_inappwebview?.callHandler) {
      targetUrl.searchParams.set("hide_sdui_header", "true");
      openAckoDriveDeeplink(targetUrl.toString());
      return;
    }

    window.location.href = targetUrl.toString();
  };

  if (!is_visible) return null;

  return (
    <Page>
      <DesktopHeader>
        <Header id={props.id} />
      </DesktopHeader>
      <DesktopBody>
        <DesktopProgress>
          <ProgressSection steps={DESKTOP_PROGRESS_STEPS} />
        </DesktopProgress>
        <Shell>
          <TopSection>
            {shouldShowBackButton && (
              <Nav>
                <BackButton
                  aria-label="Go back"
                  type="button"
                  onClick={handleBack}
                >
                  <img
                    src={resolvePath("/images/back-icon.svg")}
                    alt=""
                    width={24}
                    height={24}
                  />
                </BackButton>
              </Nav>
            )}
            <PlansAvailable>
              {displayPlansCount} {plansLabel}
            </PlansAvailable>
            <InsuranceHeading>
              {insuranceHeading ||
                `Insurance plans for your ${displayVehicleName} starts from ${displayStartingPremium}`}
            </InsuranceHeading>
            <PrimaryButton type="button" onClick={handleSeeAllPlans}>
              {primaryCtaLabel}
            </PrimaryButton>
          </TopSection>

          <DriveSection>
            <Logo
              src={resolvePath(DEFAULT_LOGO)}
              alt="ACKO Drive"
            />
            <DriveHeading>
              {ackoDriveHeading ||
                `Save on your new ${displayVehicleName}, not just your insurance`}
            </DriveHeading>
            <SavingsCard>
              <HeroVisual>
                <CarBackground
                  src={resolvePath(CAR_BACKGROUND_FACES)}
                  alt=""
                  aria-hidden="true"
                />
                <CarImage
                  src={carImageUrl || FALLBACK_CAR_IMAGE}
                  alt={displayVehicleName}
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).src =
                      FALLBACK_CAR_IMAGE;
                  }}
                />
              </HeroVisual>

              <Copy>
                <strong>{displayCustomersCount} customers</strong> who bought
                this exact variant on <strong>ACKO Drive</strong> paid{" "}
                <span>
                  {displaySavings
                    ? `${displaySavings} less than dealer price.`
                    : "less than dealer price."}
                </span>
              </Copy>

              <PriceComparison>
                <PriceColumn>
                  <PriceLabel green>ACKO Drive price</PriceLabel>
                  <PriceValue>{displayAckoDrivePrice || "--"}</PriceValue>
                </PriceColumn>
                <PriceColumn>
                  <PriceLabel>Avg. dealer price</PriceLabel>
                  <PriceValue>{displayDealerPrice || "--"}</PriceValue>
                </PriceColumn>
                <Vs>VS</Vs>
              </PriceComparison>

              <SecondaryButton type="button" onClick={handleSeeHow}>
                {secondaryCtaLabel}
              </SecondaryButton>
            </SavingsCard>
          </DriveSection>
        </Shell>
      </DesktopBody>
    </Page>
  );
};

export default connect(mapStateToProps)(AckoDriveUpsellPage);
